import type { UploadLayout, UploadLayoutDefinition } from '@erp/rpc';

import { BUILT_IN_LAYOUT_EPOCH } from './supplierLayouts';

/**
 * The Rockwell WG Results layout, offered as a template when a new layout is
 * created. It reproduces all 4,107 rows of the reference
 * `UPLOAD - Header Cross.xlsx` built from `WG - Results 10-01-2026.xlsx`
 * (#7756); `rockwellLayout.test.ts` pins it.
 */
export const ROCKWELL_LAYOUT_NAME = 'Rockwell';

const Y = { kind: 'constant', value: 'Y' } as const;
const text = (header: string) =>
  ({ kind: 'source', header, transform: { type: 'text' } }) as const;
const date = (header: string) =>
  ({ kind: 'source', header, transform: { type: 'date' } }) as const;
const filled = (header: string) =>
  ({ kind: 'flag', when: { header }, value: 'Y' }) as const;

export const ROCKWELL_LAYOUT: UploadLayoutDefinition = {
  includeWhenAnyFilled: ['LOAD UNDER AS400 ITEM', 'LOAD UNDER AS400 PCSC'],
  columns: [
    { column: 'A', mapping: { kind: 'rowNumber' } },
    { column: 'B', mapping: { kind: 'constant', value: 'C' } },
    { column: 'C', mapping: text('AS400 TITLE') },
    { column: 'D', mapping: text('AS400 LINE2') },
    { column: 'E', mapping: Y },
    { column: 'F', mapping: text('AS400 LINE3') },
    { column: 'G', mapping: Y },
    { column: 'H', mapping: date('SAP START') },
    { column: 'I', mapping: date('SAP END') },
    { column: 'R', mapping: text('AS400 ACCOUNT') },
    { column: 'S', mapping: filled('AS400 ACCOUNT') },
    { column: 'T', mapping: text('AS400 GROUP') },
    { column: 'U', mapping: filled('AS400 GROUP') },
    { column: 'V', mapping: text('PRIOR SPA') },
    { column: 'W', mapping: Y },
    { column: 'X', mapping: { kind: 'constant', value: 'MASS LOAD ND' } },
    { column: 'Y', mapping: Y },
    { column: 'Z', mapping: text('NEW SPA') },
    { column: 'AA', mapping: Y },
    { column: 'AL', mapping: text('LOAD UNDER AS400 ITEM') },
    {
      column: 'AM',
      mapping: {
        kind: 'source',
        header: 'LOAD UNDER AS400 PCSC',
        transform: { type: 'substring', start: 0, length: 2 },
      },
    },
    {
      column: 'AN',
      mapping: {
        kind: 'source',
        header: 'LOAD UNDER AS400 PCSC',
        transform: { type: 'substring', start: 2, length: null },
      },
    },
    { column: 'AR', mapping: { kind: 'constant', value: '1' } },
    {
      column: 'AS',
      mapping: {
        kind: 'formula',
        expression:
          '((1 + [SAP COST] / 100) * (1 + DEFAULT([PRIOR COST ADDER], 0)) / (1 - [PRIOR MARGINS]) - 1) * 100',
        decimals: 2,
        blankWhenZero: false,
      },
    },
    { column: 'BJ', mapping: { kind: 'constant', value: '1' } },
    {
      column: 'BK',
      mapping: {
        kind: 'source',
        header: 'SAP COST',
        transform: { type: 'number', decimals: 2 },
      },
    },
    { column: 'BL', mapping: text('AS400 PRIOR ROUNDUP') },
    {
      column: 'CC',
      mapping: {
        kind: 'formula',
        expression:
          '(1 + [SAP COST] / 100) * DEFAULT([PRIOR COST ADDER], 0) * 100',
        decimals: 2,
        blankWhenZero: true,
      },
    },
    {
      column: 'CD',
      mapping: { kind: 'flag', when: { column: 'CC' }, value: 'Y' },
    },
  ],
};

/**
 * The template as a ready supplier, offered until the org saves its own
 * Rockwell layout (which then replaces it): a user with read access can
 * convert a Rockwell file without anyone first saving a layout.
 */
export const BUILT_IN_ROCKWELL_LAYOUT: UploadLayout = {
  id: '00000000-0000-4000-8000-000000000001',
  name: ROCKWELL_LAYOUT_NAME,
  definition: ROCKWELL_LAYOUT,
  createdAt: BUILT_IN_LAYOUT_EPOCH,
  updatedAt: BUILT_IN_LAYOUT_EPOCH,
};
